// The enum handle comes from the proto package directly, not via connect-client:
// this module is bundled into a Node test, and connect-client reads
// import.meta.env at module scope. The Session import is type-only and erased.
import { Integrations } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import type { Session } from '@vitesse/base-ui';
import type { EndpointOp } from './endpoint-builder';

/*
 * The two integration-page gating decisions that are NOT a plain "wrap the
 * control in <vitesse-gated>", pulled out so bench/integration-access.test.ts can
 * pin them without a browser (the same reason integration-form-model.ts exists).
 *
 * Everything else on the page IS a plain wrap — Edit on Apply, Delete on Delete,
 * the Paths tab on BrowseSchema / IntegrationIndex.Subscribe — and is declared
 * inline at the control, where the check-gating script can see it. These two
 * are different: one picks WHICH procedure a button stands for, the other is a
 * read with no control at all.
 */

/** The narrowest session surface these decisions need — so a test can pass a
 *  fake without building an Identity or a Plan. */
export type Allower = Pick<Session, 'allowsRpc'>;

/**
 * The procedure the builder's one button stands for.
 *
 * A read op's Invoke does not call Read directly — it scopes the observe-lease
 * onto the op's leaves and the RESPONSE arrives on the value stream, which the
 * api-gateway refuses without integration:read. A write op's Send is
 * Integrations.Write. Same button, two policies; the lock must name the one
 * that will actually refuse.
 */
export function invokeRpc(op: Pick<EndpointOp, 'write'>) {
  return op.write ? Integrations.method.write : Integrations.method.read;
}

/**
 * May this session's value stream be opened — the "browse but not read" case.
 *
 * The Paths tab is gated on browse, so a person holding browse without read
 * reaches the grid. The catalogue of paths is theirs to see; the live values
 * are not, and the server enforces exactly that (values.go
 * requireIntegrationReadForValues). This is the UI declining to open a stream
 * it knows will be refused — and, just as importantly, declining to issue the
 * tagged page fetches that would extend an observe-lease on the customer's
 * external system for a caller an admin has cut off.
 *
 * FAIL-OPEN with no session, matching Session and <vitesse-gated>: a missing
 * session is a wiring bug, and blank values for a paying customer is the worse
 * failure. The server still refuses.
 */
export function mayReadValues(session: Allower | undefined): boolean {
  if (!session) return true;
  return session.allowsRpc(Integrations.method.read);
}
